import { useMemo, useState, type ReactElement } from "react";
import {
  ContainmentPlanParamsSchema,
  MAX_CONTAINMENT_ACTIONS,
  toLocalIsoDate,
  type ContainmentPlanAction,
  type ContainmentPlanParams,
} from "@playbookai/widget-schemas";
import { DatePicker } from "../../molecules/DatePicker/DatePicker.js";
import type { WidgetBodyProps, WidgetChangeOptions } from "../types.js";
import { useFieldEditSession } from "../field-edit-session.js";
import { ContainmentSummary } from "./Component.js";
import { ActionDetailPanel } from "./ActionDetailPanel.js";
import { ActionList, type ActionFilters } from "./ActionList.js";
import { shortDate, sortByAttention } from "./presentation.js";

const NO_FILTERS: ActionFilters = {
  search: "",
  status: "all",
  effectiveness: "all",
  priority: "all",
};

export function ContainmentPlanExpandedEditor({
  data,
  onChange,
  readOnly,
}: WidgetBodyProps<ContainmentPlanParams>): ReactElement {
  const [filters, setFilters] = useState<ActionFilters>(NO_FILTERS);
  const [requestedId, setRequestedId] = useState<string | undefined>(undefined);
  // PB-4923 AC5 — the plan's two scalar leaves. `nextReviewDate` is edited
  // through the DS `DatePicker`, which now reports its OPEN lifetime, so it
  // claims for as long as the calendar is up rather than going unprotected.
  const cadenceLease = useFieldEditSession("revisitCadenceDays");
  const nextReviewLease = useFieldEditSession("nextReviewDate");

  const visibleActions = useMemo(
    () => sortByAttention(data.actions.filter(matches(filters))),
    [data.actions, filters],
  );
  // Selection is presentation state: it survives edits, and falls back to the
  // top of the current view when the requested action is deleted or filtered out.
  const selected =
    visibleActions.find((action) => action.id === requestedId) ??
    visibleActions[0];

  // Returns the host's answer, so a person pick can wait on its save (d25).
  const emit = (
    candidate: ContainmentPlanParams,
    options?: WidgetChangeOptions,
  ): void | Promise<void> => {
    if (readOnly) return;
    const parsed = ContainmentPlanParamsSchema.safeParse(candidate);
    if (!parsed.success) return;
    return options ? onChange(parsed.data, options) : onChange(parsed.data);
  };
  const updateAction = (
    actionId: string,
    patch: Partial<ContainmentPlanAction>,
    options?: WidgetChangeOptions,
  ): void | Promise<void> =>
    emit(
      {
        ...data,
        actions: data.actions.map((action) =>
          action.id === actionId ? { ...action, ...patch } : action,
        ),
      },
      options,
    );
  const addAction = (): void => {
    if (readOnly || data.actions.length >= MAX_CONTAINMENT_ACTIONS) return;
    const id = nextId("containment");
    setRequestedId(id);
    setFilters(NO_FILTERS);
    emit({
      ...data,
      actions: [
        ...data.actions,
        {
          id,
          action: "New containment action",
          owner: "",
          eta: toLocalIsoDate(new Date()),
          priority: "Medium",
          status: "Open",
          effectiveness: "Not yet assessed",
          notes: [],
        },
      ],
    });
  };
  const deleteAction = (actionId: string): void => {
    setRequestedId(undefined);
    emit({
      ...data,
      actions: data.actions.filter((action) => action.id !== actionId),
    });
  };

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col gap-4 overflow-y-auto">
      <div className="flex min-w-0 flex-wrap items-start justify-between gap-3">
        <ContainmentSummary data={data} />
        <details className="min-w-0 text-xs text-pb-text-muted">
          <summary className="cursor-pointer">
            Review settings ·{" "}
            {data.nextReviewDate
              ? `Next review ${shortDate(data.nextReviewDate)}`
              : "Next review not set"}
            {data.revisitCadenceDays
              ? ` · Every ${data.revisitCadenceDays} days`
              : ""}
          </summary>
          <div className="mt-3 flex flex-wrap items-end gap-3">
            <label className="text-dense-1 text-pb-text-muted">
              Review cadence (days)
              <input
                aria-label="Review cadence days"
                type="number"
                min={1}
                max={365}
                disabled={readOnly}
                value={data.revisitCadenceDays ?? ""}
                {...cadenceLease}
                onChange={(event) =>
                  emit({
                    ...data,
                    revisitCadenceDays:
                      event.target.value === ""
                        ? undefined
                        : Number(event.target.value),
                  })
                }
                className="mt-1 block w-20 rounded-md border border-pb-control-border bg-pb-card px-2 py-1 text-xs text-pb-text"
              />
            </label>
            <div className="w-44 min-w-0">
              <DatePicker
                label="Next review"
                value={data.nextReviewDate ?? ""}
                disabled={readOnly === true}
                clearable
                clearLabel="Clear next review"
                {...nextReviewLease}
                onChange={(iso) => {
                  emit({ ...data, nextReviewDate: iso });
                }}
              />
            </div>
          </div>
        </details>
      </div>

      <div className="grid min-h-0 min-w-0 flex-1 gap-3 lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]">
        <ActionList
          actions={visibleActions}
          total={data.actions.length}
          filters={filters}
          selectedId={selected?.id}
          readOnly={readOnly === true}
          canAdd={data.actions.length < MAX_CONTAINMENT_ACTIONS}
          onFiltersChange={setFilters}
          onSelect={setRequestedId}
          onAdd={addAction}
        />
        {selected ? (
          <ActionDetailPanel
            key={selected.id}
            action={selected}
            readOnly={readOnly}
            onUpdate={(patch, options) =>
              updateAction(selected.id, patch, options)
            }
            onDelete={() => deleteAction(selected.id)}
          />
        ) : (
          <div className="grid min-h-48 place-items-center rounded-lg border border-dashed border-pb-border px-4 text-center text-sm text-pb-text-muted">
            {data.actions.length === 0
              ? "Add a containment action to start protecting the customer."
              : "Select a containment action to review its protection, verification and notes."}
          </div>
        )}
      </div>
    </div>
  );
}

function matches(
  filters: ActionFilters,
): (action: ContainmentPlanAction) => boolean {
  const query = filters.search.trim().toLowerCase();
  return (action) => {
    if (filters.status !== "all" && action.status !== filters.status) {
      return false;
    }
    if (
      filters.effectiveness !== "all" &&
      action.effectiveness !== filters.effectiveness
    ) {
      return false;
    }
    if (filters.priority !== "all" && action.priority !== filters.priority) {
      return false;
    }
    if (query === "") return true;
    return [
      action.action,
      action.owner,
      action.protectedScope ?? "",
      action.verificationMethod ?? "",
      action.successCriteria ?? "",
    ].some((field) => field.toLowerCase().includes(query));
  };
}

function nextId(prefix: string): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return `${prefix}-${crypto.randomUUID()}`;
  }
  return `${prefix}-${Date.now().toString(36)}`;
}
